import { Button } from '@/components/ui/button';
import { MobileCarousel } from '@/components/MobileCarousel';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Search, Shield, FileCheck, Star, Award } from 'lucide-react';

export default function TrademarkSearchPage({ onCTAClick }) {
  return (
    <div>
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1697301439916-169bd6844842?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-accent mb-4">Trademark Search Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-primary mb-6 leading-tight">
              Comprehensive Trademark Search Services to Avoid Conflicts
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Avoid costly trademark conflicts with our professional comprehensive trademark search services. We conduct thorough searches of USPTO databases, state records, and common law sources to identify potential conflicts before you file your trademark application.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" onClick={onCTAClick}>
              Order Trademark Search
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                Professional Trademark Name Search Services
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                A comprehensive trademark search is essential before filing a trademark application. Our trademark name search services identify existing trademarks that could conflict with your proposed mark, helping you avoid USPTO rejections, costly legal disputes, and potential infringement claims. We search federal USPTO databases, state trademark registries, common law sources, business name databases, domain name registrations, and social media platforms to provide a complete picture of trademark availability.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our trademark search reports include detailed analysis of identical and similar marks, assessment of potential conflicts, risk evaluation for your proposed trademark, recommendations for proceeding with registration, and alternative trademark suggestions if needed. Whether you're launching a new brand name, registering a company logo, or protecting a product name, our trademark search services provide the critical information you need to make informed decisions before investing in trademark registration.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img src="https://images.unsplash.com/photo-1589330694653-ded6df03f754" alt="Trademark search documents" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Why Trademark Searches Are Critical
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Avoid USPTO Rejections', desc: 'Identify conflicting trademarks before filing to avoid application rejections and wasted filing fees.' },
              { icon: Award, title: 'Prevent Legal Disputes', desc: 'Discover potential trademark conflicts early to avoid costly infringement lawsuits and cease-and-desist letters.' },
              { icon: FileCheck, title: 'Save Time & Money', desc: 'Investing in a trademark search saves thousands in potential legal fees and rebranding costs down the road.' },
              { icon: Search, title: 'Comprehensive Coverage', desc: 'Our searches cover federal, state, common law, and international databases for complete trademark availability assessment.' },
              { icon: Check, title: 'Risk Assessment', desc: 'Receive detailed analysis of trademark conflicts with clear risk evaluations and actionable recommendations.' },
              { icon: Shield, title: 'Strategic Planning', desc: 'Make informed branding decisions based on comprehensive trademark availability data and expert analysis.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <item.icon className="h-12 w-12 text-accent mb-6" />
                <h3 className="text-xl font-bold text-primary mb-4">{item.title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {[...Array(6)].map((_, i) => i < 3 && (
        <section key={i} className={`py-20 md:py-32 ${i % 2 === 0 ? '' : 'bg-background-surface'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                {['What Our Trademark Search Includes', 'Simple 3-Step Search Process', 'Client Success Stories'][i]}
              </h2>
            </div>
            {i === 0 && (
              <MobileCarousel className="grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  'USPTO Federal Database Search', 'State Trademark Registry Search', 
                  'Common Law Source Research', 'Business Name Database Search',
                  'Domain Name Availability Check', 'Detailed Risk Assessment Report'
                ].map((title, j) => (
                  <div key={j} className="bg-white border border-border p-8">
                    <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                      <Check className="h-6 w-6 text-accent" />{title}
                    </h3>
                    <p className="text-base leading-relaxed text-muted-foreground">
                      Comprehensive analysis of {title.toLowerCase()} to identify potential conflicts and availability issues.
                    </p>
                  </div>
                ))}
              </MobileCarousel>
            )}
            {i === 1 && (
              <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
                {['Submit Search Request', 'We Conduct Search', 'Receive Detailed Report'].map((title, j) => (
                  <div key={j} className="bg-white border border-border p-8 relative">
                    <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">{j+1}</div>
                    <div className="mt-6">
                      <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">{title}</h3>
                      <p className="text-base leading-relaxed text-muted-foreground">
                        {['Provide your proposed trademark and business details through our online form.', 
                          'Our experts search USPTO, state, and common law databases for conflicts.',
                          'Get a comprehensive report with risk assessment and recommendations.'][j]}
                      </p>
                    </div>
                  </div>
                ))}
              </MobileCarousel>
            )}
            {i === 2 && (
              <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
                {[1,2,3].map((n) => (
                  <div key={n} className="bg-white border border-border p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, k) => (<Star key={k} className="h-5 w-5 fill-accent text-accent" />))}
                    </div>
                    <p className="text-base leading-relaxed text-muted-foreground mb-6">
                      "The trademark search identified a conflict we missed. Their detailed report and recommendations saved us from a costly trademark rejection!"
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                        {['TH', 'NK', 'BP'][n-1]}
                      </div>
                      <div>
                        <div className="font-semibold text-primary">{['Tom Harris', 'Nina Kumar', 'Brian Peterson'][n-1]}</div>
                        <div className="text-sm text-muted-foreground">Business Owner</div>
                      </div>
                    </div>
                  </div>
                ))}
              </MobileCarousel>
            )}
          </div>
        </section>
      ))}

      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Trademark Search FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'Why do I need a trademark search?', a: 'A trademark search identifies existing marks that could conflict with yours, helping avoid USPTO rejections, legal disputes, and infringement claims. It provides critical information for making informed branding decisions.' },
              { q: 'How long does a trademark search take?', a: 'Most trademark searches are completed within 2-5 business days. Complex searches involving international databases or extensive common law research may take slightly longer.' },
              { q: 'What does a trademark search report include?', a: 'Our reports include search results from USPTO, state, and common law databases, analysis of identical and similar marks, risk assessment for conflicts, likelihood of registration success, and recommendations for proceeding.' },
              { q: 'Can I do a trademark search myself?', a: 'While the USPTO database is publicly accessible, professional trademark searches cover additional sources and include expert legal analysis that helps identify potential conflicts that non-experts often miss.' }
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i+1}`} className="bg-white border border-border px-8">
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Search Your Trademark?
          </h2>
          <p className="text-lg leading-relaxed text-white/90 mb-8">
            Get a comprehensive trademark search report before filing. Order your search today!
          </p>
          <Button size="lg" onClick={onCTAClick} className="bg-accent hover:bg-accent/90 text-primary">
            Order Trademark Search Now
          </Button>
        </div>
      </section>
    </div>
  );
}
